import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'

import {
    fetchProjectEpicOptions,
    getCachedProjectEpicOptions,
} from './epicOptionsCache'

import styles from './epicSelector.module.css'

const EpicPickerPanel = ({
    projectId,
    value,
    onChange,
    options: controlledOptions,
    placeholder = 'Select epic',
    disabled = false,
    enabled = true,
    active = false,
    showEpicKey = true,
}) => {
    const [fetchedOptions, setFetchedOptions] = useState([])
    const [loading, setLoading] = useState(false)

    const isControlled = Array.isArray(controlledOptions)
    const options = isControlled ? controlledOptions : fetchedOptions

    useEffect(() => {
        if (isControlled || !enabled || !projectId || !active) {
            return undefined
        }

        let cancelled = false

        // Show last known list immediately, then always refetch on open
        // so newly created epics appear after leaving Sprint view.
        const cached = getCachedProjectEpicOptions(projectId)
        if (cached) {
            setFetchedOptions(cached)
            setLoading(false)
        } else {
            setLoading(true)
        }

        const loadEpics = async () => {
            try {
                const list = await fetchProjectEpicOptions(projectId, {
                    force: true,
                })
                if (!cancelled) {
                    setFetchedOptions(list)
                }
            } catch {
                if (!cancelled && !cached) {
                    setFetchedOptions([])
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        loadEpics()

        return () => {
            cancelled = true
        }
    }, [projectId, enabled, isControlled, active])

    const handleSelect = (item) => {
        if (disabled) return
        onChange?.(item.value)
    }

    return (
        <div className={styles.panel}>
            <div className={styles.panelInner}>
                <div className={styles.panelHeader}>{placeholder}</div>
                <div className={styles.panelScroll}>
                    {loading ? (
                        <div className={styles.panelMessage}>Loading epics…</div>
                    ) : options.length === 0 ? (
                        <div className={styles.panelMessage}>
                            No epics available
                        </div>
                    ) : (
                        options.map((item, index) => {
                            const isSelected =
                                item?.value != null &&
                                value != null &&
                                String(item.value) === String(value)

                            return (
                                <div
                                    key={
                                        item.value != null
                                            ? String(item.value)
                                            : `${item.label}-${index}`
                                    }
                                    role="option"
                                    aria-selected={isSelected}
                                    className={`${styles.panelItem} ${
                                        isSelected ? styles.panelItemSelected : ''
                                    }`}
                                    onClick={() => handleSelect(item)}
                                >
                                    <div className={styles.panelItemRow}>
                                        <span
                                            className={styles.checkSlot}
                                            aria-hidden
                                        >
                                            {isSelected ? (
                                                <Icon
                                                    icon="mdi:check"
                                                    width={18}
                                                    height={18}
                                                    className={styles.checkIcon}
                                                />
                                            ) : null}
                                        </span>
                                        <span className={styles.epicMeta}>
                                            {showEpicKey && item.epicKey ? (
                                                <span className={styles.epicKey}>
                                                    {item.epicKey}
                                                </span>
                                            ) : null}
                                            <span className={styles.epicTitle}>
                                                {item.label}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            )
                        })
                    )}
                </div>
            </div>
        </div>
    )
}

export default EpicPickerPanel
